import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { BeforeAfterSlider } from '~/components/before-after-slider'
import {
  ArrowLeft, ArrowRight, Euro, Clock, MapPin, Camera, Wrench, CheckCircle,
} from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const { AVANT_APRES_PROJECTS, AVANT_APRES_CATEGORIES } = await import('~/lib/avant-apres-data.server')
  const project = AVANT_APRES_PROJECTS.find((p) => p.slug === params.slug)
  if (!project) throw new Response('Not Found', { status: 404 })

  const related = AVANT_APRES_PROJECTS
    .filter((p) => p.slug !== project.slug && p.category === project.category)
    .slice(0, 3)

  return { project, categoryLabel: AVANT_APRES_CATEGORIES[project.category], related }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []
  const p = data.project
  return [
    { title: `${p.title} — Avant/Après | AfficheServices` },
    { name: 'description', content: `${p.shortDesc} Budget ${p.budget}, durée ${p.duration}. ${p.location}.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/avant-apres/${p.slug}` },
  ]
}

export default function AvantApresDetailPage() {
  const { project, categoryLabel, related } = useLoaderData<typeof loader>()
  const p = project

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Avant / Après', url: 'https://www.afficheservices.fr/avant-apres' },
        { name: p.title, url: `https://www.afficheservices.fr/avant-apres/${p.slug}` },
      ]} />

      <section className="bg-gradient-to-br from-rose-900 via-fuchsia-900 to-slate-900 px-4 py-12 text-white sm:py-16">
        <div className="mx-auto max-w-4xl">
          <Link to="/avant-apres" className="inline-flex items-center gap-1 text-xs font-semibold text-rose-200 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Tous les projets
          </Link>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-rose-200">
            <Camera className="h-3 w-3" /> {categoryLabel}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">{p.title}</h1>
          <p className="mt-3 max-w-2xl text-base text-rose-200">{p.shortDesc}</p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6">
        {/* Interactive before/after comparator */}
        <BeforeAfterSlider
          beforeImage={p.beforeImage}
          afterImage={p.afterImage}
          alt={p.title}
          className="ring-1 ring-border"
        />

        {/* Key facts */}
        <div className="mt-8 grid gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-border bg-white p-4">
            <p className="flex items-center gap-1 text-xs font-semibold text-slate-500">
              <Euro className="h-3 w-3" /> Budget
            </p>
            <p className="mt-1 text-sm font-bold text-slate-900">{p.budget}</p>
          </div>
          <div className="rounded-xl border border-border bg-white p-4">
            <p className="flex items-center gap-1 text-xs font-semibold text-slate-500">
              <Clock className="h-3 w-3" /> Durée
            </p>
            <p className="mt-1 text-sm font-bold text-slate-900">{p.duration}</p>
          </div>
          <div className="rounded-xl border border-border bg-white p-4">
            <p className="flex items-center gap-1 text-xs font-semibold text-slate-500">
              <MapPin className="h-3 w-3" /> Lieu
            </p>
            <p className="mt-1 text-sm font-bold text-slate-900">{p.location}</p>
          </div>
          <div className="rounded-xl border border-border bg-white p-4">
            <p className="flex items-center gap-1 text-xs font-semibold text-slate-500">
              <Wrench className="h-3 w-3" /> Métier
            </p>
            <p className="mt-1 text-sm font-bold text-slate-900">{p.metier}</p>
          </div>
        </div>

        {/* Description */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-slate-900">Détail du chantier</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{p.description}</p>
        </section>

        {/* Key works */}
        <section className="mt-10">
          <h2 className="text-xl font-bold text-slate-900">Principales interventions</h2>
          <ul className="mt-4 space-y-2">
            {p.keyWorks.map((w, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {w}
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="mt-12 rounded-2xl border border-border bg-gradient-to-br from-rose-50 to-fuchsia-50 p-6 text-center">
          <h2 className="text-lg font-bold text-slate-900">Un projet similaire en tête ?</h2>
          <p className="mt-1 text-sm text-slate-600">
            Comparez 3 devis gratuits d'artisans vérifiés près de chez vous.
          </p>
          <Link
            to="/devis-gratuit"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white hover:bg-primary-hover"
          >
            Recevoir mes devis <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-lg font-bold text-slate-900">Autres projets {categoryLabel}</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/avant-apres/${r.slug}`}
                  className="group overflow-hidden rounded-xl border border-border bg-white transition-all hover:shadow-md"
                >
                  <img src={r.afterImage} alt={r.title} className="h-32 w-full object-cover" loading="lazy" />
                  <div className="p-3">
                    <p className="text-xs font-semibold text-slate-900 line-clamp-2 group-hover:text-primary">
                      {r.title}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-400">{r.budget}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <p className="mt-12 text-center text-[11px] text-slate-400">
          Photographies d'illustration (stock). Les budgets et durées correspondent aux moyennes du marché
          pour ce type de chantier.
        </p>
      </div>

      <Footer />
    </div>
  )
}
